/**
 * Durable key/value storage, on every surface.
 *
 * The outbox's whole guarantee is that something typed is written down *before* the network is
 * tried, so this cannot be an in-memory map with a persistence nicety bolted on. On the web that is
 * localStorage, which is synchronous; on a phone it is AsyncStorage, which is not. The async
 * signature is therefore the shared one — a synchronous API here would be a lie on native, and the
 * one place a lie about durability costs something is exactly this one.
 *
 * `getSync` exists only for the first paint: a shell that has to await storage before it can decide
 * whether the device is paired flashes an unpaired screen at a paired device on every cold start.
 * It answers from the cache `load()` fills, and from localStorage directly on web.
 */
import { Platform } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';

const isWeb = Platform.OS === 'web';

/** Everything read at startup, so `getSync` can answer without awaiting. */
const cache = new Map<string, string | null>();

export async function getItem(key: string): Promise<string | null> {
  if (isWeb) {
    const value = safeLocal(() => window.localStorage.getItem(key), null);
    cache.set(key, value);
    return value;
  }

  try {
    const value = await AsyncStorage.getItem(key);
    cache.set(key, value);
    return value;
  } catch {
    return cache.get(key) ?? null;
  }
}

export async function setItem(key: string, value: string): Promise<void> {
  cache.set(key, value);

  if (isWeb) {
    safeLocal(() => window.localStorage.setItem(key, value), undefined);
    return;
  }

  try {
    await AsyncStorage.setItem(key, value);
  } catch {
    // Storage full or blocked. The value is still in the cache, so nothing is lost while the app
    // lives; there is nowhere better to put it. Same bargain the PWA's outbox made.
  }
}

export async function removeItem(key: string): Promise<void> {
  cache.delete(key);

  if (isWeb) {
    safeLocal(() => window.localStorage.removeItem(key), undefined);
    return;
  }

  try {
    await AsyncStorage.removeItem(key);
  } catch {
    // As above.
  }
}

/** What was read at startup, or on web what localStorage says right now. Never awaits. */
export function getSync(key: string): string | null {
  if (isWeb) return safeLocal(() => window.localStorage.getItem(key), null);
  return cache.get(key) ?? null;
}

/** Pre-reads keys so {@link getSync} can answer for them on native. */
export async function preload(keys: string[]): Promise<void> {
  await Promise.all(keys.map((key) => getItem(key)));
}

/** localStorage throws outright in a few privacy modes rather than merely failing to persist. */
function safeLocal<T>(body: () => T, fallback: T): T {
  try {
    return body();
  } catch {
    return fallback;
  }
}
